import type { ILogger, IObservabilityProvider, ObservedError } from '../types'
import { matchUserOutcome } from '@safe-global/utils/services/exceptions/normalizeError'
import {
  datadogRum,
  type RumEvent,
  type RumErrorEvent,
  type RumResourceEvent,
  type RumEventDomainContext,
  type RumErrorEventDomainContext,
} from '@datadog/browser-rum'
import {
  COMMIT_HASH,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_RUM_CLIENT_TOKEN,
  DATADOG_RUM_DEFAULT_PRIVACY_LEVEL,
  DATADOG_RUM_ENV,
  DATADOG_RUM_SERVICE,
  DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
  DATADOG_RUM_SESSION_SAMPLE_RATE,
  DATADOG_RUM_SITE,
  DATADOG_RUM_TRACE_SAMPLE_RATE,
  DATADOG_RUM_TRACK_LONG_TASKS,
  DATADOG_RUM_TRACK_RESOURCES,
  DATADOG_RUM_TRACK_USER_INTERACTIONS,
  DATADOG_RUM_TRACING_ENABLED,
  GATEWAY_URL_PRODUCTION,
  GATEWAY_URL_STAGING,
  IS_TEST_E2E,
} from '@/config/constants'

type DatadogSite =
  | 'datadoghq.com'
  | 'datadoghq.eu'
  | 'us3.datadoghq.com'
  | 'us5.datadoghq.com'
  | 'ddog-gov.com'
  | 'ap1.datadoghq.com'

export const isDatadogEnabled = Boolean(DATADOG_RUM_APPLICATION_ID) && Boolean(DATADOG_RUM_CLIENT_TOKEN) && !IS_TEST_E2E

const EXTENSION_URL_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'webkit-masked-url://',
]

const KNOWN_NOISE_PATTERNS = [
  // Firefox fires this when ResizeObserver hits a benign infinite-loop guard
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  // Null/undefined promise rejections from injected 3rd-party scripts
  'Non-Error promise rejection captured with value: null',
  'Non-Error promise rejection captured with value: undefined',
  // Safari Intelligent Tracking Prevention noise
  'The operation is insecure',
  // Generic script error surfaced when a cross-origin script fails — unactionable
  'Script error.',
]

const originatesFromExtension = (stack: string | undefined): boolean => {
  if (!stack) return false
  return EXTENSION_URL_PATTERNS.some((pattern) => stack.includes(pattern))
}

const isKnownNoise = (message: string | undefined): boolean => {
  if (!message) return false
  return KNOWN_NOISE_PATTERNS.some((pattern) => message.includes(pattern))
}

// Our own webpack output — anything reported from here is first-party and must
// stay visible even if its `error.type` happens to be `EvalError` (see below).
const FIRST_PARTY_BUNDLE_PATH = '_next/static/'

/**
 * `error.type === 'EvalError'` is not proof of a CSP-blocked eval() on its
 * own: a thrown string `"EvalError: …"`, an explicit `new EvalError()`, or any
 * custom class that sets `name = 'EvalError'` all produce the same `type` in
 * `@datadog/browser-core`'s `computeRawError` (`stackTrace.name`). So this is
 * additionally gated on the stack *not* pointing at our own bundle
 * (`_next/static/`) — mirrors `originatesFromExtension`'s pattern-matching,
 * inverted. We ship no eval()/Function() call of our own today (grepped
 * `apps/web/src` and `packages/`), so a same-bundle `EvalError` would mean
 * something new started calling eval() and is worth surfacing, not silencing.
 * A missing stack defaults to "not first-party": we have no eval() call site
 * of our own to attribute it to, so there is nothing here that a stack could
 * be pointing at.
 */
const isNotFirstPartyStack = (stack: string | undefined): boolean => {
  if (!stack) return true
  return !stack.includes(FIRST_PARTY_BUNDLE_PATH)
}

/**
 * Real-world `EvalError`s reaching RUM are third-party vendor scripts (Beamer,
 * GTM, Calendly, Cloudflare Turnstile, HubSpot forms, etc.) whose eval() /
 * `new Function()` call our `script-src` CSP correctly blocks in production —
 * the block is the intended behaviour and nothing is broken for the user, so
 * it is dropped rather than counted as a real error (WA-2952).
 *
 * Trade-off this accepts: `'unsafe-eval'` **is** permitted in dev/Cypress
 * (`config/securityHeaders.ts`), so a first-party eval()/Function() call would
 * pass locally and only throw — as this same `EvalError` type — in
 * production, where this filter would otherwise hide it. Accepted because (a)
 * no current bundle path constructs code dynamically, and (b)
 * `isNotFirstPartyStack` still surfaces it if the stack ever does point at our
 * own `_next/static/` output.
 */
const isCspBlockedEval = (errorEvent: RumErrorEvent): boolean =>
  errorEvent.error.type === 'EvalError' && isNotFirstPartyStack(errorEvent.error.stack)

const NON_USER_IMPACTING_SOURCES = new Set(['console', 'report'])

const ADDRESS = String.raw`0x[a-fA-F0-9]{40}`
const MESSAGE_HASH = String.raw`0x[a-fA-F0-9]{64}`

// Terminates every pattern below so a prefix match cannot also swallow a deeper
// route nested under the same path, which reports its own failures.
const PATH_END = String.raw`(?:[?#]|$)`

/**
 * Resource requests whose non-2xx responses are an expected part of normal
 * operation, not failures (WA-2991). Matched on the raw request URL — the
 * `@resource.url_path_group` facet is computed by Datadog and is not available
 * client-side — plus the status code.
 */
const EXPECTED_RESOURCE_FAILURES: { urlPattern: RegExp; statuses: Set<number> }[] = [
  // "Safe not targeted." — the documented answer for nearly every Safe, on a
  // route probed on every Safe load.
  {
    urlPattern: new RegExp(
      String.raw`/v1/targeted-messaging/outreaches/[^/]+/chains/[^/]+/safes/${ADDRESS}${PATH_END}`,
    ),
    statuses: new Set([404]),
  },
  // CGW has no metadata for most addresses; the UI falls back to the raw address.
  {
    urlPattern: new RegExp(String.raw`/v1/chains/[^/]+/contracts/${ADDRESS}${PATH_END}`),
    statuses: new Set([404]),
  },
  // A documented answer on a relay-fee chain when the request quotes no
  // `safeTxHash`, not a permission failure.
  {
    urlPattern: new RegExp(String.raw`/v1/chains/[^/]+/relay/${ADDRESS}${PATH_END}`),
    statuses: new Set([403]),
  },
  // A Safe CGW does not index — an undeployed counterfactual one, or an address
  // typed into the URL. 429 on these same routes is a real capacity signal and
  // deliberately keeps reporting.
  {
    urlPattern: new RegExp(String.raw`/v1/chains/[^/]+/safes/${ADDRESS}/transactions/(?:queued|history)${PATH_END}`),
    statuses: new Set([404]),
  },
  // An expired or already-executed message the UI still has a link to.
  {
    urlPattern: new RegExp(String.raw`/v1/chains/[^/]+/messages/${MESSAGE_HASH}${PATH_END}`),
    statuses: new Set([404]),
  },
]

const isExpectedResourceFailure = (event: RumResourceEvent): boolean => {
  const { url, status_code: status } = event.resource ?? {}
  if (!url || status === undefined) return false
  return EXPECTED_RESOURCE_FAILURES.some(({ urlPattern, statuses }) => urlPattern.test(url) && statuses.has(status))
}

/**
 * Drop RUM error events that are demonstrably not caused by user-impacting
 * failures so the Error-Free Views SLO reflects real breakage, plus resource
 * events for endpoints whose non-2xx responses are expected (see
 * `EXPECTED_RESOURCE_FAILURES`). Views, actions and other resources pass
 * through untouched.
 *
 * Sources we drop:
 * - `console`: the RUM SDK auto-instruments `console.error` via
 *   `trackConsoleError` (no init flag exists to disable it). The codebase has
 *   many `console.error` catch blocks for non-blocking failures (clipboard
 *   denial, RPC retries, third-party widget init, observability self-recovery
 *   in `composite.ts`, etc.) that are not user-impacting.
 * - `report`: Browser Reporting API events (CSP violations, deprecation,
 *   intervention, permissions-policy). Useful as a security/policy signal but
 *   not indicative of user-blocking failure; CSP visibility belongs on a
 *   `report-uri`/`report-to` endpoint, not the SLO.
 *
 * We also drop, regardless of source:
 * - `EvalError`s whose stack doesn't point at our own bundle (see
 *   `isCspBlockedEval`): a third-party vendor script's eval()/Function() call
 *   correctly blocked by our `script-src` CSP (WA-2952). The CSP doing its
 *   job is not a Safe{Wallet} failure.
 *
 * Genuine user failures continue to flow through `trackError` /
 * `captureException` (source: `custom`) and unhandled exceptions (`source`).
 * A failed request is not among them — the RUM SDK raises no error event with
 * source `network` — so it reaches us only as the `resource` event above.
 */
export const filterRumEvent = (event: RumEvent, context: RumEventDomainContext): boolean => {
  if (event.type === 'resource') return !isExpectedResourceFailure(event as RumResourceEvent)
  if (event.type !== 'error') return true

  const errorEvent = event as RumErrorEvent
  if (NON_USER_IMPACTING_SOURCES.has(errorEvent.error.source)) return false
  if (isKnownNoise(errorEvent.error.message)) return false
  if (isCspBlockedEval(errorEvent)) return false
  if (originatesFromExtension(errorEvent.error.stack)) return false

  // User-driven outcomes surfaced as unhandled errors by third-party SDKs
  // (WalletConnect TTL expiry, a wallet's bare "Rejected" reply) never pass
  // through trackError/the normalizer. Re-emit them as info-level actions —
  // kept queryable as an approval-flow drop-off signal — and drop the RUM
  // error so they stay off the Error-Free Views SLO (WA-2950).
  const userOutcome = matchUserOutcome(errorEvent.error.message)
  if (userOutcome) {
    datadogRum.addAction(errorEvent.error.message, { level: 'info', error_type: userOutcome })
    return false
  }

  // context.error is the raw value originally passed to addError/captureException
  const { error: rawError } = context as RumErrorEventDomainContext
  const rawStack = rawError instanceof Error ? rawError.stack : undefined
  if (originatesFromExtension(rawStack)) return false

  return true
}

export class DatadogProvider implements IObservabilityProvider {
  readonly name = 'Datadog'
  private isInitialized = false

  async init(): Promise<void> {
    const isClient = typeof window !== 'undefined'
    if (!isClient || !isDatadogEnabled || this.isInitialized) {
      return
    }

    try {
      const getInitConfiguration = datadogRum.getInitConfiguration
      const isAlreadyInitialized = typeof getInitConfiguration === 'function' && Boolean(getInitConfiguration())
      if (isAlreadyInitialized) {
        this.isInitialized = true
        return
      }

      datadogRum.init({
        applicationId: DATADOG_RUM_APPLICATION_ID,
        clientToken: DATADOG_RUM_CLIENT_TOKEN,
        site: DATADOG_RUM_SITE as DatadogSite,
        service: DATADOG_RUM_SERVICE,
        env: DATADOG_RUM_ENV,
        version: COMMIT_HASH,
        sessionSampleRate: DATADOG_RUM_SESSION_SAMPLE_RATE,
        sessionReplaySampleRate: DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
        trackUserInteractions: DATADOG_RUM_TRACK_USER_INTERACTIONS,
        trackResources: DATADOG_RUM_TRACK_RESOURCES,
        trackLongTasks: DATADOG_RUM_TRACK_LONG_TASKS,
        defaultPrivacyLevel: DATADOG_RUM_DEFAULT_PRIVACY_LEVEL,
        // Pinned to the v6 default. v7 flipped this to `true`, which routes
        // auto-collected click action names through the privacy tree walker:
        // under our `defaultPrivacyLevel: 'mask'`, `shouldMaskNode` rejects
        // every node, so every action name would resolve to an empty string.
        enablePrivacyForActionName: false,
        beforeSend: filterRumEvent,
        ...(DATADOG_RUM_TRACING_ENABLED && {
          traceSampleRate: DATADOG_RUM_TRACE_SAMPLE_RATE,
          // Pinned to the v6 default. v7 flipped this to `true`, adding a
          // `baggage` header to every traced request — CGW would have to
          // allowlist it in `Access-Control-Allow-Headers` or all preflights
          // to the gateway start failing. Flip back on once CGW accepts it.
          propagateTraceBaggage: false,
          allowedTracingUrls: [
            { match: GATEWAY_URL_PRODUCTION, propagatorTypes: ['tracecontext', 'datadog'] },
            { match: GATEWAY_URL_STAGING, propagatorTypes: ['tracecontext', 'datadog'] },
          ],
        }),
      })

      this.isInitialized = true
    } catch (error) {
      console.warn('Failed to initialize Datadog RUM (might be already initialized):', error)
    }
  }

  getLogger(): ILogger {
    return {
      info: (message: string, context?: Record<string, unknown>) => {
        if (this.isInitialized) {
          datadogRum.addAction(message, { level: 'info', ...context })
        }
      },
      warn: (message: string, context?: Record<string, unknown>) => {
        if (this.isInitialized) {
          datadogRum.addAction(message, { level: 'warn', ...context })
        }
      },
      error: (message: string, context?: Record<string, unknown>) => {
        if (this.isInitialized) {
          datadogRum.addError(new Error(message), context)
        }
      },
      debug: (message: string, context?: Record<string, unknown>) => {
        if (this.isInitialized) {
          datadogRum.addAction(message, { level: 'debug', ...context })
        }
      },
    }
  }

  captureError({ error, isUserFacing, tags }: ObservedError): void {
    // Only user-facing failures become RUM errors (addError) so background /
    // logged errors don't count against the Error-Free Views SLO — those are
    // already recorded as warn-level actions via getLogger().warn.
    if (this.isInitialized && isUserFacing) {
      datadogRum.addError(error, tags)
    }
  }
}
